import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import PushNotificationSettings from "@/components/game/PushNotificationSettings";
import AuthPage from "@/pages/AuthPage";
import LandingPage from "@/pages/LandingPage";
import MainMenu from "@/pages/MainMenu";
import CharacterCreation from "@/pages/CharacterCreation";
import GameView from "@/pages/GameView";
import AdminPage from "@/pages/AdminPage";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import Contact from "@/pages/legal/Contact";
import About from "@/pages/legal/About";
import Hall from "@/pages/Hall";
import HallDetail from "@/pages/HallDetail";
import ambientMusic from "@/utils/AmbientMusic";

const API = process.env.REACT_APP_BACKEND_URL;

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="font-heading text-[#cfb53b] text-lg">Loading...</p>
        </div>
      </div>
    );
  }
  return user ? children : <Navigate to="/auth" />;
};

// Iter 91/92 — `/` is a public landing page for anonymous visitors.
// Authenticated users see the MainMenu directly so the existing in-app
// navigation (logout → /, sidebar logo → /) keeps working as before.
// Iter 92 — when `?draft=1` is present we ALWAYS show the LandingPage
// (even to admins) so the admin CMS preview iframe works correctly.
const HomeRoute = () => {
  const { user, loading } = useAuth();
  const isDraftPreview = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("draft") === "1";
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c]">
        <div className="w-12 h-12 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (isDraftPreview) return <LandingPage />;
  return user ? <MainMenu /> : <LandingPage />;
};

function App() {
  // Iter 90 — load the admin-managed audio library once at app boot so
  // the ambient music engine can shuffle through curated tracks per mood.
  useEffect(() => {
    if (API) ambientMusic.loadLibrary(API);
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            {/* Public legal / info pages — no auth required so they're
                reachable from the login screen, Google index & future custom
                domains. */}
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/cookies" element={<CookiePolicy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            {/* Hall of Stories — public showcase, browseable without login */}
            <Route path="/hall" element={<Hall />} />
            <Route path="/hall/:pid" element={<HallDetail />} />
            <Route path="/" element={<HomeRoute />} />
            <Route path="/menu" element={<ProtectedRoute><MainMenu /></ProtectedRoute>} />
            <Route path="/create" element={<ProtectedRoute><CharacterCreation /></ProtectedRoute>} />
            <Route path="/game/:gameId" element={<ProtectedRoute><GameView /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          <Toaster position="top-right" theme="dark" richColors />
          <PWAInstallPrompt lang={(typeof window !== "undefined" && localStorage.getItem("ac_lang")) || "en"} />
          <PushNotificationSettings variant="banner" lang={(typeof window !== "undefined" && localStorage.getItem("ac_lang")) || "en"} />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
